import { useCallback, useEffect, useState } from "react";
import { getWorkerCodePolicy, listWorkerCategories, listWorkerReview } from "../../../services/karkhanaApi";

export type WorkerCategory = {
  id: string;
  prefix: string;
  name: string;
  defaultPayBasis: "PIECE" | "TIME" | "JOB";
  canWeave: boolean;
  canHelp: boolean;
  nameHints: string[];
  active: boolean;
  /** HKP post its workers get as users, e.g. WEAVER; null = Karkhana only. */
  userRole?: string | null;
  /** False until the studio (or a template) chose a post; userRole is then the template default. */
  userRoleSet?: boolean;
};

export type HkpPost = { id: string; role: string; desc?: string };

/** WEAVER -> Weaver, LPG_SUPPLIER -> LPG supplier (short words stay as they are). */
export const postLabel = (role?: string | null) =>
  role
    ? role
        .split("_")
        .map((w, i) => (w.length <= 3 ? w : i === 0 ? w.charAt(0) + w.slice(1).toLowerCase() : w.toLowerCase()))
        .join(" ")
    : "";

/** A worker's post: the first of their categories that has one, as the server decides. */
export const workerPost = (categories: WorkerCategory[], categoryIds: string[] = []) => {
  for (const id of categoryIds) {
    const c = categories.find((x) => x.id === id);
    if (c?.userRole) return c.userRole;
  }
  return null;
};

export type CodePolicy = { numbering: "ORG_WIDE" | "PER_PREFIX"; format: string; nextNumber: number };

/** Loom roles first (weavers, then helpers), then the rest by prefix. */
export const sortCategories = (list: WorkerCategory[]) => {
  const rank = (c: WorkerCategory) => (c.canWeave ? 0 : c.canHelp ? 1 : 2);
  return [...list].sort((a, b) => rank(a) - rank(b) || a.prefix.localeCompare(b.prefix));
};

/** Categories, code policy, and how many workers wait in review. Shared by the worker tabs. */
export const useWorkerSetup = () => {
  const [categories, setCategories] = useState<WorkerCategory[]>([]);
  const [policy, setPolicy] = useState<CodePolicy | null>(null);
  const [reviewCount, setReviewCount] = useState(0);
  const [loaded, setLoaded] = useState(false);

  const reload = useCallback(async () => {
    const [cats, pol, review]: any[] = await Promise.all([
      listWorkerCategories(),
      getWorkerCodePolicy(),
      listWorkerReview({ limit: 1 }),
    ]);
    setCategories(cats?.success && Array.isArray(cats.data) ? sortCategories(cats.data) : []);
    setPolicy(pol?.success ? pol.data : null);
    setReviewCount(review?.success ? Number(review.data?.length || 0) : 0);
    setLoaded(true);
  }, []);

  useEffect(() => {
    reload().catch(() => setLoaded(true));
  }, [reload]);

  return { categories, policy, reviewCount, loaded, reload };
};

/** Preview of the next generated code, e.g. K_542. Mirrors the server's {prefix}_{n} format. */
export const previewCode = (policy: CodePolicy | null, prefix?: string): string => {
  if (!policy || !prefix) return "";
  return policy.format
    .replace(/\{n(?::(0+))\}/, (_m, pad) => String(policy.nextNumber).padStart(pad.length, "0"))
    .replace("{n}", String(policy.nextNumber))
    .replace("{prefix}", prefix.toUpperCase());
};

export const categoryLabel = (categories: WorkerCategory[], id: string) => {
  const c = categories.find((x) => x.id === id);
  return c ? `${c.prefix} · ${c.name}` : "";
};
